import React, { useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { InteractablePopover } from 'ui-components/components/interactable-popover';
import { MDash } from 'ui-components/components/mdash';
import { Text, TextType } from 'ui-components/components/text';
import { emptyArray } from 'ui-components/default-props/empty';

import { INVENTORY_PRODUCT_COLUMN_HOVER } from '../../../../constants/analytics-events';
import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import { getParsedFloatValueOrDefault, useSellerSetting } from '../../../../hooks/use-seller-setting';
import Global from '../../../../store/global-selectors';
import { usePatchedInventoryData } from '../total-available';

import '../../../../prop-types/product-grid/product';
import messages from '../../messages';
import style from './style.module.less';
import { Segment } from '../../../../services/analytics-services/segment';

const name = <FormattedMessage {...messages.inventoryColumnHeader} />;

// Reorder Threshold value to use when neither a product-specific not a seller-wide "Default" Reorder Threshold is set
const defaultDefaultReorderThreshold = 5;

const InventoryPopup = ({ aggregateInventoryCounts, isDanger, isWarning, noReorderThresholdSet, reorderThreshold, stock }) => {
  const { onhandQuantity, committedQuantity, connectionCounts = emptyArray } = aggregateInventoryCounts;
  const hasMultipleSources = connectionCounts.length > 1;
  const reorderThresholdText = noReorderThresholdSet ? `${reorderThreshold}*` : `${reorderThreshold}`;
  return (
    <FlexColumn className={style['popup-content']}>
      {isWarning ? (
        <BannerMessageV2
          content={<Text textType={TextType.Caption} value={messages.lowStockAlert} />}
          layout={BannerLayout.Inline}
          status={BannerStatus.Warning}
        />
      ) : null}
      {isDanger ? (
        <BannerMessageV2
          content={<Text textType={TextType.Caption} value={messages.criticalStockAlert} />}
          layout={BannerLayout.Inline}
          status={BannerStatus.Error}
        />
      ) : null}
      <FlexRow className={style['popup-row']}>
        <Text textType={TextType.Caption} value={messages.onHandLabel} />
        <Text textType={TextType.Caption} value={`${onhandQuantity}`} />
      </FlexRow>
      {hasMultipleSources
        ? connectionCounts.map(({ connectionId, connectionName, onhandQuantity: connectionOnhandQuantity }) => (
            <FlexRow key={connectionId} className={classnames(style['popup-row'], style['nested-row'])}>
              <Text textType={TextType.Caption} value={`${connectionName}:`} />
              <Text textType={TextType.Caption} value={`${connectionOnhandQuantity}`} />
            </FlexRow>
          ))
        : null}
      <FlexRow className={style['popup-row']}>
        <Text textType={TextType.Caption} value={messages.committedLabel} />
        <Text textType={TextType.Caption} value={`${committedQuantity}`} />
      </FlexRow>
      <FlexRow className={style['popup-row']}>
        <Text textType={TextType.Caption} value={messages.inStockLabel} />
        <Text textType={TextType.Caption} value={`${stock}`} />
      </FlexRow>
      <FlexRow className={classnames(style['popup-row'], style.divider, style['below-divider'])}>
        <Text textType={TextType.Caption} value={messages.reorderThresholdLabel} />
        <Text textType={TextType.Caption} value={reorderThresholdText} />
      </FlexRow>
      {noReorderThresholdSet ? (
        <FlexRow className={classnames(style['popup-row'], style['below-divider'])}>
          <Text textType={TextType.Caption} value={messages.reorderThresholdNotSet} />
        </FlexRow>
      ) : null}
    </FlexColumn>
  );
};

const getStatus = (isDanger, isWarning) => {
  if (isDanger) {
    return 'Danger';
  }
  if (isWarning) {
    return 'Warning';
  }
  return 'Healthy';
};

/**
 * @param {ProductRowData} props
 * @returns {JSX.Element}
 */
const ProgressBar = (props) => {
  const sellerId = useSelector(Global.selectSellerSellerId);
  const {
    aggregateInventoryCounts,
    inventoryDisabled,
    reorderThreshold: productReorderThreshold,
    isBundle = false,
  } = usePatchedInventoryData(props);
  const { availableQuantity, excludedQuantity, onhandQuantity, connectionCounts = emptyArray } = aggregateInventoryCounts;

  const sellerDefaultReorderThreshold = getParsedFloatValueOrDefault(useSellerSetting(SellerSettings.InventoryReOrderThreshold), 0);
  const sellerSpecifiedReorderThreshold = productReorderThreshold || sellerDefaultReorderThreshold;

  const noSellerSpecifiedReorderThresholdSet = !sellerSpecifiedReorderThreshold;
  const fullyDefaultedReorderThreshold = sellerSpecifiedReorderThreshold || defaultDefaultReorderThreshold;

  const stock = availableQuantity - excludedQuantity;

  // The column consists of a bar that is 100% filled when the product has 3x the reorder threshold as available to sell.
  // It is made of 6 segments and each segments represents 1/2 of the reorder threshold available.
  const filledSegments = stock / (fullyDefaultedReorderThreshold / 2.0);
  const isDanger = filledSegments <= 2;
  const isWarning = !isDanger && filledSegments <= 4;

  const trackOpen = useCallback(() => {
    Segment.trackEvent(INVENTORY_PRODUCT_COLUMN_HOVER, {
      sellerId,
      status: getStatus(isDanger, isWarning),
    });
  }, [sellerId, isDanger, isWarning]);

  if (inventoryDisabled || isBundle || onhandQuantity === undefined || connectionCounts === emptyArray) {
    return (
      <FlexRow className={style.stock}>
        <MDash />
      </FlexRow>
    );
  }

  const progressBarStyles = classnames(style['progress-bar'], {
    [style.danger]: isDanger,
    [style.warning]: isWarning,
  });

  return (
    <FlexRow className={style.stock}>
      <InteractablePopover
        ignoreFlyout
        onHoverOpen={trackOpen}
        popoverBody={
          <InventoryPopup
            aggregateInventoryCounts={aggregateInventoryCounts}
            isDanger={isDanger}
            isWarning={isWarning}
            noReorderThresholdSet={noSellerSpecifiedReorderThresholdSet}
            reorderThreshold={fullyDefaultedReorderThreshold}
            stock={stock}
          />
        }
        preferPlace='top'
        title='Inventory'
      >
        <FlexRow className={progressBarStyles}>
          <div className={classnames({ [style.empty]: filledSegments <= 0 })} />
          <div className={classnames({ [style.empty]: filledSegments <= 1 })} />
          <div className={classnames({ [style.empty]: filledSegments <= 2 })} />
          <div className={classnames({ [style.empty]: filledSegments <= 3 })} />
          <div className={classnames({ [style.empty]: filledSegments <= 4 })} />
          <div className={classnames({ [style.empty]: filledSegments <= 5 })} />
        </FlexRow>
      </InteractablePopover>
    </FlexRow>
  );
};

export default {
  getMessageDescriptor: () => messages.inventoryColumnHeader,
  name,
  render: ProgressBar,
};
